// Phone Calendar (view 'calendar') — window.PhoneCalendar, plus the meeting
// row + detail sheet the Today page shares (window.PhoneCalKit).
//
// A day agenda under a Mon–Sun week strip (swipe or ‹ › for other weeks).
// One /api/calendar/events fetch per week — the same window and params the
// desktop week view asks for — normalized by window.AtlasCal (views/
// calendar.jsx), so times, all-day placement and link state read exactly as
// on desktop. Tapping a meeting opens a sheet with what you need walking
// into it: when/where, Join, the linked company/contact, attendees; the
// desktop event drawer (log a note · follow up · link to a record) opens from
// there as a full-screen sheet.

const PC_TAP = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };

// Web links go through the native shell when there is one, so Teams/Zoom
// join links launch their apps; mailto navigates in place.
const pcOpen = (url) => {
  if (!url) return;
  if (window.AtlasNative && window.AtlasNative.isNative) { window.AtlasNative.openExternal(url); return; }
  if (/^(tel|mailto):/i.test(url)) window.location.href = url;
  else window.open(url, '_blank', 'noopener');
};

const pcCal = () => window.AtlasCal;
const pcTimeRange = (ev) => (ev.is_all_day || !ev._startD
  ? 'All day'
  : `${pcCal().calFmtTime(ev._startD)} – ${pcCal().calFmtTime(ev._endD)}`);
const pcIsLive = (ev, now = Date.now()) => !ev.is_all_day && ev._start <= now && ev._end > now;
const pcAttendeeLabel = (a) => (typeof a === 'string' ? a : (a && (a.name || a.email)) || '');
// Who the meeting is with, in words — the link state the desktop shows as
// chip colors and pills.
const pcWho = (ev) => {
  const rec = [ev.company && ev.company.name, ev.contact && ev.contact.name].filter(Boolean).join(' · ');
  if (rec) return rec;
  if (ev.internal_only) return 'Internal';
  return 'Not linked to a record';
};
// Left bar color: linked (accent) · internal (slate) · needs linking (amber).
const pcBar = (ev) => (ev.linked ? 'var(--accent)' : ev.internal_only ? 'var(--border-strong)' : 'var(--amber-fg)');

// Bucket a list of normalized events by local day key — all-day events by
// the raw date substring, same rule as the desktop grid (a UTC-midnight
// all-day event would otherwise land a day early west of UTC).
const pcBucket = (events) => {
  const by = {};
  (events || []).forEach((e) => {
    const key = e.is_all_day ? (e.start || '').slice(0, 10) : (e._startD ? pcCal().calDayKey(e._startD) : null);
    if (!key) return;
    (by[key] = by[key] || []).push(e);
  });
  Object.values(by).forEach((list) => list.sort((a, b) =>
    (b.is_all_day ? 1 : 0) - (a.is_all_day ? 1 : 0) || a._start - b._start));
  return by;
};

// ── Meeting row ─────────────────────────────────────────────────────────────
const PhoneEventRow = ({ ev, onOpen, now }) => {
  const live = pcIsLive(ev, now);
  const past = !ev.is_all_day && ev._end && ev._end <= (now || Date.now());
  const ext = (ev.external_attendees || []).length;
  const sub = [pcWho(ev), ext ? `${ext} guest${ext === 1 ? '' : 's'}` : null,
    ev.transcript_url ? 'transcript' : null, ev.has_note ? 'note logged' : null].filter(Boolean).join(' · ');
  return (
    <button type="button" onClick={() => onOpen(ev)}
      style={{
        ...PC_TAP, display: 'flex', alignItems: 'stretch', gap: 12, width: '100%', minHeight: 60,
        padding: '10px 16px', border: 'none', borderBottom: '1px solid var(--border)', background: 'var(--surface)',
        textAlign: 'left', cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit', opacity: past ? 0.6 : 1,
      }}>
      <span style={{ width: 62, flexShrink: 0, paddingTop: 1 }}>
        {ev.is_all_day ? (
          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--text-2)' }}>All day</span>
        ) : (
          <>
            <span className="num" style={{ display: 'block', fontSize: 14, fontWeight: 600, color: live ? 'var(--accent-text)' : 'var(--text)' }}>{pcCal().calFmtTime(ev._startD)}</span>
            <span className="num" style={{ display: 'block', fontSize: 12.5, color: 'var(--text-3)', marginTop: 1 }}>{pcCal().calFmtTime(ev._endD)}</span>
          </>
        )}
      </span>
      <span aria-hidden style={{ width: 3, borderRadius: 2, flexShrink: 0, background: pcBar(ev) }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere', fontSize: 15.5, fontWeight: 500, lineHeight: 1.3 }}>{ev.title}</span>
        <span style={{ display: 'block', fontSize: 13, lineHeight: 1.35, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: !ev.linked && !ev.internal_only ? 'var(--amber-fg)' : 'var(--text-3)' }}>{sub}</span>
      </span>
      {live && <span style={{ alignSelf: 'center', flexShrink: 0 }}><Pill hue="blue" sz="sm" dot>Now</Pill></span>}
      <span style={{ alignSelf: 'center', flexShrink: 0, display: 'inline-flex', color: 'var(--text-4)' }}><Icon.ChevronR size={14} /></span>
    </button>
  );
};

// ── Meeting sheet ───────────────────────────────────────────────────────────
const PhoneEventSheet = ({ ev, onClose, activeUser, goToCompany, goToContact, onChanged }) => {
  const [shown, setShown] = React.useState(ev);
  const [drawerEv, setDrawerEv] = React.useState(null);   // desktop event drawer (note · follow up · link)
  React.useEffect(() => { if (ev) setShown(ev); }, [ev]);
  const e = shown;
  const go = (fn) => { onClose(); fn(); };
  const C = pcCal();
  const upcoming = !!(e && e._end && e._end > Date.now());
  const ext = (e && e.external_attendees) || [];
  const all = ((e && e.attendees) || []).map(pcAttendeeLabel).filter(Boolean);
  const extSet = new Set(ext.map((a) => pcAttendeeLabel(a).toLowerCase()));
  const internal = all.filter((a) => !extSet.has(a.toLowerCase()));

  const detail = (label, value) => value ? (
    <div style={{ display: 'flex', gap: 12, padding: '5px 0', fontSize: 14.5, lineHeight: 1.4 }}>
      <span style={{ width: 64, flexShrink: 0, color: 'var(--text-3)' }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, color: 'var(--text)', overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  ) : null;

  const linkRows = e ? [
    e.company && { key: 'co', label: e.company.name || 'Company', sub: 'Company', icon: <Icon.Building size={18} />, onClick: () => go(() => goToCompany(e.company.id)) },
    e.contact && { key: 'ct', label: e.contact.name || 'Contact', sub: 'Contact', icon: <Icon.User size={18} />, onClick: () => go(() => goToContact(e.contact.id)) },
  ].filter(Boolean) : [];
  const actionRows = e ? [
    { key: 'more', label: e.linked ? 'Add a note or follow-up' : 'Link, note or follow-up',
      sub: e.linked ? 'Log the meeting on the record, or set a reminder' : 'Link the meeting to a company or contact first',
      icon: <Icon.Edit size={18} />, onClick: () => { onClose(); setDrawerEv(e); } },
    e.external_url && { key: 'outlook', label: 'Open in Outlook', icon: <Icon.External size={18} />, onClick: () => pcOpen(e.external_url) },
    e.transcript_url && { key: 'tx', label: 'Transcript', sub: 'Fireflies', icon: <Icon.Doc size={18} />, onClick: () => pcOpen(e.transcript_url) },
  ].filter(Boolean) : [];

  return (
    <>
      <window.PhoneSheet open={!!ev} onClose={onClose} title="Meeting">
        {e && (
          <>
            <div style={{ padding: '0 18px 12px' }}>
              <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', lineHeight: 1.35, overflowWrap: 'anywhere' }}>{e.title}</div>
              <div style={{ marginTop: 8 }}>
                {/* All-day: the date substring, never a tz-converted Date (see pcBucket). */}
                {detail('When', e.is_all_day
                  ? `${(e.start || '').length >= 10 ? C.calDayLabel(new Date(`${e.start.slice(0, 10)}T00:00:00`)) : ''} · all day`
                  : `${C.calDayLabel(e._startD)} · ${pcTimeRange(e)}${e.end_is_estimate ? ' (approx.)' : ''}`)}
                {detail('Where', e.location && /^https?:\/\//i.test(e.location)
                  // Plain link: the browser opens a tab; in the shell native.jsx
                  // routes it (an onClick here would open it a second time).
                  ? <a href={e.location} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>{e.location}</a>
                  : e.location)}
                {detail('With', !e.linked ? (e.internal_only ? 'Internal meeting — no outside party' : <span style={{ color: 'var(--amber-fg)' }}>Not linked to a record</span>) : null)}
              </div>
              {e.join_url && upcoming && (
                <button type="button" onClick={() => pcOpen(e.join_url)}
                  style={{ ...PC_TAP, marginTop: 10, width: '100%', height: 48, borderRadius: 12, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Icon.Play size={16} /> Join meeting
                </button>
              )}
            </div>
            {linkRows.length > 0 && (
              <>
                <window.PhoneSectionHeader title="Linked to" style={{ padding: '6px 18px 2px' }} />
                <window.PhoneActionList items={linkRows} />
              </>
            )}
            {ext.length > 0 && (
              <>
                <window.PhoneSectionHeader title="Guests" count={ext.length} style={{ padding: '6px 18px 2px' }} />
                <window.PhoneActionList items={ext.map((a) => {
                  const label = pcAttendeeLabel(a);
                  const isEmail = /@/.test(label);
                  return {
                    key: label, label, icon: <Icon.Mail size={18} />,
                    sub: isEmail ? 'Email' : null,
                    onClick: isEmail ? () => pcOpen(`mailto:${label}`) : undefined,
                  };
                })} />
              </>
            )}
            {internal.length > 0 && (
              <div style={{ padding: '0 18px 10px', fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.45, overflowWrap: 'anywhere' }}>
                Also invited: {internal.join(', ')}
              </div>
            )}
            <div style={{ borderTop: '1px solid var(--border)' }}>
              <window.PhoneActionList items={actionRows} />
            </div>
          </>
        )}
      </window.PhoneSheet>
      {drawerEv && window.CalEventDrawer && (
        <window.CalEventDrawer
          ev={drawerEv}
          activeUser={activeUser}
          onClose={() => setDrawerEv(null)}
          // Stay open after Link / Save, like desktop: linking is the step
          // before writing the note, so closing here sent the user back to
          // find the meeting again. The list behind refreshes.
          onChanged={(link) => {
            setDrawerEv((d) => (d ? {
              ...d, linked: true,
              ...(link && link.company ? { company: link.company } : null),
              ...(link && link.contact ? { contact: link.contact } : null),
            } : d));
            onChanged && onChanged();
          }}
          goToCompany={(id) => { setDrawerEv(null); goToCompany(id); }}
          goToContact={(id) => { setDrawerEv(null); goToContact(id); }}
        />
      )}
    </>
  );
};

// ── The page ────────────────────────────────────────────────────────────────
const pcMidnight = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const PC_WEEKDAY = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const PhoneCalendar = ({ activeUser, goToCompany, goToContact }) => {
  const C = pcCal();
  const [day, setDay] = React.useState(() => pcMidnight(new Date()));
  const [events, setEvents] = React.useState(null);   // the selected week's, normalized; null = loading
  const [error, setError] = React.useState(null);
  const [openEv, setOpenEv] = React.useState(null);
  const [now, setNow] = React.useState(Date.now());
  const seq = React.useRef(0);
  const weekStart = C.calWeekStart(day);
  const wsKey = weekStart.getTime();

  const load = React.useCallback(async (quiet) => {
    const mine = ++seq.current;
    if (!quiet) { setEvents(null); setError(null); }
    const start = new Date(wsKey);
    const end = new Date(start); end.setDate(end.getDate() + 7);
    try {
      const res = await window.AtlasApi.calendarEvents({ start: start.toISOString(), end: end.toISOString() });
      if (mine !== seq.current) return;
      setEvents(C.normalizeEvents(res));
      setError(null);
      setNow(Date.now());
    } catch (e) {
      if (mine !== seq.current) return;
      if (!quiet) { setEvents([]); setError(e.message || 'Could not load meetings'); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsKey]);
  React.useEffect(() => { load(false); }, [load]);
  window.PhoneTaskKit.useRefresh(() => load(true), ['atlas-data-changed']);
  // Keep "Now" / past dimming honest while the page sits open.
  React.useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);

  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const byDay = React.useMemo(() => pcBucket(events), [events]);
  const todayKey = C.calDayKey(new Date(now));
  const dayKey = C.calDayKey(day);
  const list = byDay[dayKey] || [];

  const shiftWeek = (n) => setDay((d) => { const x = new Date(d); x.setDate(x.getDate() + n * 7); return x; });
  const goToday = () => setDay(pcMidnight(new Date()));

  // Horizontal swipe on the week strip → previous / next week.
  const swipe = React.useRef(null);
  const onTouchStart = (e) => { const p = e.touches[0]; swipe.current = { x: p.clientX, y: p.clientY }; };
  const onTouchEnd = (e) => {
    const s = swipe.current; swipe.current = null;
    if (!s) return;
    const p = e.changedTouches[0];
    const dx = p.clientX - s.x, dy = p.clientY - s.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) shiftWeek(dx < 0 ? 1 : -1);
  };

  const navBtn = (label, icon, onClick) => (
    <button type="button" aria-label={label} onClick={onClick}
      style={{ ...PC_TAP, width: 44, height: 44, border: 'none', background: 'transparent', color: 'var(--text-2)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</button>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexShrink: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '4px 4px 0 16px' }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-display)' }}>
            {day.toLocaleDateString([], { month: 'long', year: 'numeric' })}
          </div>
          {dayKey !== todayKey && (
            <button type="button" onClick={goToday}
              style={{ ...PC_TAP, height: 44, padding: '0 4px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
              <span style={{ height: 32, padding: '0 12px', borderRadius: 999, border: '1px solid var(--border)', display: 'inline-flex', alignItems: 'center', color: 'var(--accent-text)', fontSize: 14, fontWeight: 600 }}>Today</span>
            </button>
          )}
          {navBtn('Previous week', <Icon.ChevronL size={20} />, () => shiftWeek(-1))}
          {navBtn('Next week', <Icon.ChevronR size={20} />, () => shiftWeek(1))}
        </div>
        <div role="tablist" aria-label="Days of the week" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
          style={{ display: 'flex', padding: '2px 8px 8px', touchAction: 'pan-y' }}>
          {days.map((d, i) => {
            const k = C.calDayKey(d);
            const sel = k === dayKey;
            const isToday = k === todayKey;
            const has = (byDay[k] || []).length > 0;
            return (
              <button key={k} type="button" role="tab" aria-selected={sel} onClick={() => setDay(pcMidnight(d))}
                aria-label={d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
                style={{ ...PC_TAP, flex: 1, minWidth: 0, height: 64, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-3)' }}>{PC_WEEKDAY[i]}</span>
                <span className="num" style={{
                  width: 34, height: 34, borderRadius: 17, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 17, fontWeight: sel || isToday ? 600 : 500,
                  background: sel ? (isToday ? 'var(--accent)' : 'var(--text)') : 'transparent',
                  color: sel ? (isToday ? '#fff' : 'var(--bg)') : isToday ? 'var(--accent-text)' : 'var(--text)',
                }}>{d.getDate()}</span>
                <span style={{ width: 5, height: 5, borderRadius: 3, background: has ? 'var(--text-4)' : 'transparent' }} />
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <window.PhoneScroll>
          <window.PhoneSectionHeader
            title={dayKey === todayKey ? `Today · ${day.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}` : day.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
            count={events && !error ? list.length || null : null} />
          {events === null && <div style={{ borderTop: '1px solid var(--border)' }}><SkeletonRows rows={5} /></div>}
          {events !== null && error && (
            <window.PhoneEmpty title="Couldn't load your meetings" body={error}
              action={<Btn variant="outline" onClick={() => load(false)}>Try again</Btn>} />
          )}
          {events !== null && !error && list.length === 0 && (
            <window.PhoneEmpty title={`No meetings ${dayKey === todayKey ? 'today' : 'this day'}`} />
          )}
          {events !== null && !error && list.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border)' }}>
              {list.map((ev) => <PhoneEventRow key={ev.id} ev={ev} now={now} onOpen={setOpenEv} />)}
            </div>
          )}
        </window.PhoneScroll>
      </div>

      <PhoneEventSheet ev={openEv} onClose={() => setOpenEv(null)} activeUser={activeUser}
        goToCompany={goToCompany} goToContact={goToContact} onChanged={() => load(true)} />
    </div>
  );
};

window.PhoneCalKit = { EventRow: PhoneEventRow, EventSheet: PhoneEventSheet, bucket: pcBucket, isLive: pcIsLive, timeRange: pcTimeRange, open: pcOpen };
window.PhoneCalendar = PhoneCalendar;
